import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { SideNav, GlobalHeader } from './components';
import { useIsPlatformAdmin } from '@/providers/AuthProvider';
import { useTenant } from '@/providers/TenantProvider';
import { Button } from '@/shared/components/ui/button';
import { AppFooter } from '@/shared/components/AppFooter';
import { cn } from '@/lib/utils';

type AppShellLayoutProps = {
  /** Force platform banner (PlatformLayout) */
  platform?: boolean;
};

/** Shared app shell with responsive collapsible sidebar. */
export function AppShellLayout({ platform = false }: AppShellLayoutProps) {
  const navigate = useNavigate();
  const isPlatformAdmin = useIsPlatformAdmin();
  const { hasTenant, tenantName, clearActiveTenant } = useTenant();
  const location = useLocation();

  const onPlatformRoute = location.pathname.startsWith('/platform');
  const showPlatformBanner = platform || onPlatformRoute || (isPlatformAdmin && !hasTenant);
  const showTenantScopedBanner = isPlatformAdmin && hasTenant && !onPlatformRoute;

  function exitTenant() {
    clearActiveTenant();
    navigate('/platform/tenants');
  }

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <SideNav />
      <div className="flex min-w-0 flex-1 flex-col">
        <GlobalHeader />
        <main
          className={cn(
            'flex-1 overflow-auto bg-background p-4 md:p-6',
            'animate-in fade-in-0 duration-300'
          )}
        >
          {showPlatformBanner && (
            <div className="mb-4 rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-xs text-primary animate-in slide-in-from-top-1 duration-300">
              Platform administration mode
            </div>
          )}
          {showTenantScopedBanner && (
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-900 dark:text-amber-100 animate-in slide-in-from-top-1 duration-300">
              <span>
                Managing tenant <strong>{tenantName}</strong> — scoped admin access. Exit anytime to
                return to the platform briefing.
              </span>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={exitTenant}>
                Exit tenant
              </Button>
            </div>
          )}
          <Outlet />
        </main>
        <AppFooter />
      </div>
    </div>
  );
}
